// InvoiceTidakAktif.jsx
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Search, Eye } from "lucide-react";
import { getAuth } from "firebase/auth";
import InvoiceViewModal from "../others/InvoiceViewModal"; // adjust path if needed

const API_BASE = ""; // same origin

async function getFirebaseIdToken() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return null;
  return await user.getIdToken(false);
}

async function apiFetch(path, options = {}) {
  const token = await getFirebaseIdToken();
  if (!token) throw new Error("No token provided (user not logged in)");

  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
    credentials: "include",
    ...options,
  });

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null);

  if (!res.ok) {
    const message =
      (data && typeof data === "object" && (data.message || data.error)) ||
      (typeof data === "string" && data) ||
      `Request failed (${res.status})`;
    throw new Error(message);
  }

  return data;
}
async function apiFetchBlob(path, options = {}) {
  const token = await getFirebaseIdToken();
  if (!token) throw new Error("No token provided (user not logged in)");

  const headers = {
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };

  // only set content-type if body exists (POST json)
  if (options.body && !("Content-Type" in headers)) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: "include",
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Request failed (${res.status})`);
  }
  return await res.blob();
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "invoice.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function handleDownloadInvoicePdf(invoiceId, filenameBase = "invoice", options = {}) {
  const includeSignature = !!options.includeSignature;
  const inactive = !!options.inactive;

  const blob = await apiFetchBlob(`/api/invoice-tracker/invoices/${invoiceId}/pdf`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ includeSignature, inactive }),
  });

  downloadBlob(blob, `${filenameBase}.pdf`);
}


function formatCurrency(amount) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(Number(amount) || 0);
}

function formatDate(dateString) {
  if (!dateString) return "-";
  const dt = new Date(dateString);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const ALLOWED_STATUSES = ["void", "cancelled"];
const ALLOWED_STATUSES_SET = new Set(ALLOWED_STATUSES);
const PER_PAGE_OPTIONS = [10, 25, 50, 100];

export default function InvoiceTidakAktif({ isActive }) {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);

  const [q, setQ] = useState("");

  // filters (status + customer)
  const [statusFilter, setStatusFilter] = useState(""); // "" | "void" | "cancelled"
  const [customerFilter, setCustomerFilter] = useState(""); // selected customer name

  // pagination
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Prevent overlapping requests
  const abortRef = useRef(null);

  const loadInactiveInvoices = useCallback(async () => {
    // abort previous request if any
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const data = await apiFetch("/api/invoice-tracker/invoices", {
        method: "GET",
        signal: controller.signal,
      });
      const list = Array.isArray(data) ? data : data?.items || [];

      // only "void" and "cancelled"
      const onlyInactive = list.filter((inv) => {
        const st = String(inv.status || "").toLowerCase();
        return ALLOWED_STATUSES_SET.has(st);
      });

      setInvoices(onlyInactive);
      setPage(1);
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.warn("loadInactiveInvoices failed:", err?.message || err);
      alert(`Gagal memuat invoice tidak aktif: ${err?.message || err}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Fetch when component first mounts
  useEffect(() => {
    loadInactiveInvoices();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [loadInactiveInvoices]);

  // ✅ Fetch newest data each time user switches to this tab
  useEffect(() => {
    if (!isActive) return;
    loadInactiveInvoices();
  }, [isActive, loadInactiveInvoices]);

  // ✅ Optional: refetch when browser tab becomes visible (keeps data fresh)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible" && isActive) {
        loadInactiveInvoices();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [isActive, loadInactiveInvoices]);

  // build customer dropdown options from data
  const customerOptions = useMemo(() => {
    const set = new Set();
    (invoices || []).forEach((inv) => {
      const name = String(inv.billTo?.companyName || inv.customerName || "").trim();
      if (name) set.add(name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "id"));
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    const query = q.trim().toLowerCase();

    return (invoices || [])
      .filter((inv) => {
        const invoiceNo = String(inv.invoiceNo || inv.invoiceNumber || "").toLowerCase();
        const customerNameRaw = String(inv.billTo?.companyName || inv.customerName || "").trim();
        const customerName = customerNameRaw.toLowerCase();
        const st = String(inv.status || "").toLowerCase();

        const passQuery = !query || invoiceNo.includes(query) || customerName.includes(query);
        const passStatus = !statusFilter || st === statusFilter;
        const passCustomer = !customerFilter || customerNameRaw === customerFilter;

        return passQuery && passStatus && passCustomer;
      })
      // ✅ Sort by "Dinonaktifkan Tanggal" newest first (matches your screenshot)
      .sort((a, b) => {
        const ta = new Date(a?.voidedAt || a?.cancelledAt || a?.updatedAt || a?.createdAt || 0).getTime() || 0;
        const tb = new Date(b?.voidedAt || b?.cancelledAt || b?.updatedAt || b?.createdAt || 0).getTime() || 0;
        return tb - ta; // newest first
      });
  }, [invoices, q, statusFilter, customerFilter]);

  // reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [q, statusFilter, customerFilter, perPage]);

  const totalItems = filteredInvoices.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
  const safePage = Math.min(Math.max(1, page), totalPages);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * perPage;
    const end = start + perPage;
    return filteredInvoices.slice(start, end);
  }, [filteredInvoices, perPage, safePage]);

  const handleViewInvoice = async (invoiceId) => {
    try {
      const detail = await apiFetch(`/api/invoice-tracker/invoices/${invoiceId}`, {
        method: "GET",
      });

      // safety: ensure it's still void/cancelled
      const st = String(detail?.status || "").toLowerCase();
      if (!ALLOWED_STATUSES_SET.has(st)) {
        alert("Invoice ini sudah bukan invoice tidak aktif (void/cancelled).");
        await loadInactiveInvoices();
        return;
      }

      setSelectedInvoice(detail);
      setShowViewModal(true);
    } catch (err) {
      alert(`Gagal memuat detail invoice: ${err?.message || err}`);
    }
  };

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  const statusBadge = (status) => {
    const st = String(status || "").toLowerCase();
    if (st === "void") {
      return (
        <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          Void
        </span>
      );
    }
    if (st === "cancelled") {
      return (
        <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
          Cancelled
        </span>
      );
    }
    return (
      <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
        {status || "-"}
      </span>
    );
  };

  const fromIndex = totalItems === 0 ? 0 : (safePage - 1) * perPage + 1;
  const toIndex = Math.min(safePage * perPage, totalItems);

  return (
    <>
      {showViewModal && selectedInvoice && (
      <InvoiceViewModal
        invoice={selectedInvoice}
        onClose={() => {
          setShowViewModal(false);
          setSelectedInvoice(null);
        }}
        onEdit={null}
        onDownloadPdf={({ includeSignature }) => {
          const id = selectedInvoice?._id || selectedInvoice?.id;
          const filename = selectedInvoice?.invoiceNo || selectedInvoice?.invoiceNumber || "invoice";
          if (!id) return alert("Invoice id tidak ditemukan");

          return handleDownloadInvoicePdf(id, filename, {
          includeSignature: !!includeSignature,
          inactive: true,
        })

            .catch((err) => alert(err?.message || "Gagal download PDF"));
        }}
      />

      )}

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Invoice Tidak Aktif</h2>
            <p className="text-gray-600 mt-1">
              Riwayat invoice yang berstatus <b>void</b> atau <b>cancelled</b>
            </p>
          </div>

          {/* ✅ Refresh button removed */}
          <div className="text-sm text-gray-500">{loading ? "Loading..." : ""}</div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <div className="w-5 h-5 rounded-full bg-yellow-400 flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-yellow-900 text-xs font-bold">!</span>
          </div>
          <div>
            <h3 className="font-semibold text-yellow-900 mb-1">Perhatian</h3>
            <p className="text-sm text-yellow-800">
              Halaman ini hanya menampilkan invoice dengan status <b>void</b> atau <b>cancelled</b>. Alasan bisa dilihat
              lewat <b>Lihat Detail</b>.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col gap-3 lg:flex-row lg:gap-4 lg:items-center">
              {/* search */}
              <div className="flex-1 relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cari invoice berdasarkan nomor atau customer..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* filters */}
              <div className="flex flex-wrap gap-3 items-center justify-end">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Filter status"
                  disabled={loading}
                >
                  <option value="">Semua Status</option>
                  <option value="void">Void</option>
                  <option value="cancelled">Cancelled</option>
                </select>

                <select
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Filter Mitra/Customer"
                  disabled={loading}
                >
                  <option value="">Semua Mitra/Customer</option>
                  {customerOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>

                {/* ✅ Reset button removed */}
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-3">
              Menampilkan hanya invoice dengan status <b>void</b> atau <b>cancelled</b>.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    No. Invoice
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Jumlah
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Dinonaktifkan Tanggal
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Aksi
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {pageItems.map((inv) => {
                  const id = inv._id || inv.id;
                  const invoiceNo = inv.invoiceNo || inv.invoiceNumber || "-";
                  const customerName = inv.billTo?.companyName || inv.customerName || "-";
                  const amount = inv.totals?.grandTotal ?? inv.amount ?? 0;
                  const status = inv.status || "-";

                  const inactiveAt = inv.voidedAt || inv.cancelledAt || inv.updatedAt || inv.createdAt;

                  return (
                    <tr key={id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-center">
                        <span className="font-medium text-gray-900">{invoiceNo}</span>
                      </td>

                      <td className="px-6 py-4 text-center">{statusBadge(status)}</td>

                      <td className="px-6 py-4 text-center text-gray-700">{customerName}</td>

                      <td className="px-6 py-4 text-center">
                        <span className="font-semibold text-gray-900">{formatCurrency(amount)}</span>
                      </td>

                      <td className="px-6 py-4 text-center text-gray-700">{formatDate(inactiveAt)}</td>

                      <td className="px-6 py-4 text-center">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center text-gray-600 hover:text-gray-800"
                          title="Lihat Detail"
                          onClick={() => handleViewInvoice(id)}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {!pageItems.length && (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                      {loading ? "Memuat data..." : "Tidak ada invoice tidak aktif."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* footer: per-page selector on bottom-left + pagination on bottom-right */}
          <div className="px-6 py-4 border-t border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <select
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value) || 10)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Jumlah baris per halaman"
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} / halaman
                  </option>
                ))}
              </select>

              <div className="text-sm text-gray-600">
                Menampilkan <span className="font-semibold">{fromIndex}</span> -{" "}
                <span className="font-semibold">{toIndex}</span> dari{" "}
                <span className="font-semibold">{totalItems}</span> invoice
              </div>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={goPrev}
                className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={safePage <= 1}
              >
                Previous
              </button>

              <div className="px-3 py-1 text-sm text-gray-700">
                Halaman <span className="font-semibold">{safePage}</span> /{" "}
                <span className="font-semibold">{totalPages}</span>
              </div>

              <button
                type="button"
                onClick={goNext}
                className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={safePage >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
